/**
 * @description PIN entry screen with numeric keypad
 *
 * Key features:
 * - Shows selected employee name
 * - 4 digit PIN input with dots
 * - Numeric keypad (tablet-optimized)
 * - Error display (wrong PIN, locked account)
 * - Back button to employee select
 */

import React, { useState } from 'react';
import { ArrowLeft, Lock } from 'lucide-react';
import { useClockContext } from '../../contexts/ClockContext';
import NumericKeypad from '../shared/NumericKeypad';

const PinEntry = ({ employee, companyName, onBack }) => {
  const { login } = useClockContext();
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);
  const [remainingMinutes, setRemainingMinutes] = useState(null);

  const maxPinLength = 4;

  const handleDigit = (digit) => {
    if (pin.length < maxPinLength) {
      setPin((prev) => prev + digit);
      setError(null);
    }
  };

  const handleBackspace = () => {
    setPin((prev) => prev.slice(0, -1));
    setError(null);
  };

  const handleSubmit = async () => {
    if (pin.length !== 4) {
      setError('PIN must be 4 digits');
      return;
    }

    setLoading(true);
    setError(null);

    const result = await login(employee, pin);

    if (!result.success) {
      setPin('');
      if (result.locked) {
        setLocked(true);
        setRemainingMinutes(result.remainingMinutes);
      } else {
        setError(result.error || 'Incorrect PIN');
        if (result.attemptsRemaining !== undefined && result.attemptsRemaining <= 2) {
          setError(`Incorrect PIN. ${result.attemptsRemaining} attempt${result.attemptsRemaining !== 1 ? 's' : ''} remaining.`);
        }
      }
    }

    setLoading(false);
  };

  // Handle keyboard input
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (locked || loading) return;
      if (/^\d$/.test(e.key)) handleDigit(e.key);
      else if (e.key === 'Backspace') handleBackspace();
      else if (e.key === 'Enter') handleSubmit();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, locked, loading]);

  if (locked) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-900 px-6">
        <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mb-6 border border-red-500/20">
          <Lock className="w-10 h-10 text-red-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Account Locked</h2>
        <p className="text-slate-400 text-center mb-8 max-w-sm">
          Too many failed attempts. Please try again in {remainingMinutes || 15} minutes
          or contact your manager.
        </p>
        <button
          onClick={onBack}
          className="w-full max-w-xs px-6 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-2xl border border-slate-700 transition-all active:scale-95"
        >
          Back to Employee List
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-900 overflow-hidden">
      {/* Header */}
      <header className="absolute top-0 left-0 w-full flex items-center justify-between px-6 pt-8 pb-4">
        <button
          onClick={onBack}
          className="group flex items-center gap-2 px-3 py-2 -ml-2 text-slate-400 hover:text-white transition-all rounded-lg hover:bg-slate-800/50"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <div className="text-right">
          <p className="text-xs font-semibold text-primary-400 uppercase tracking-wider">{companyName}</p>
        </div>
      </header>

      {/* Content - Centered Container */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-md flex flex-col items-center">
          {/* Employee info */}
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-white tracking-tight">
              {employee.firstName} {employee.lastName}
            </h2>
            <p className="text-slate-400 mt-1 font-medium text-sm">Enter your 4-digit PIN</p>
          </div>

          {/* PIN dots */}
          <div className="flex gap-4 mb-6 h-10 items-center">
            {Array.from({ length: maxPinLength }).map((_, i) => (
              <div
                key={i}
                className={`w-4 h-4 rounded-full transition-all duration-300 border-2 ${
                  i < pin.length 
                    ? 'bg-primary-500 border-primary-400 shadow-[0_0_10px_rgba(var(--color-primary-500),0.5)] scale-110' 
                    : 'bg-slate-800 border-slate-700'
                }`}
              />
            ))}
          </div>

          {/* Error message */}
          <div className="h-5 mb-4">
            {error && (
              <p className="text-red-400 text-xs font-medium text-center animate-in fade-in slide-in-from-top-1">
                {error}
              </p>
            )}
          </div>

          {/* Keypad */}
          <NumericKeypad
            onDigit={handleDigit}
            onBackspace={handleBackspace}
            onSubmit={handleSubmit}
            disabled={loading}
          />

          {loading && (
            <div className="mt-6 flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-slate-400 text-xs font-medium">Verifying...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PinEntry;
